/**
 * @fileoverview KOL application detail route
 * @module routes/sys/mgmt/$segment/kols/$kolId
 */

import type { ReactElement } from 'react';
import { createFileRoute, Navigate, useNavigate } from '@tanstack/react-router';
import { AdminGuard } from '@/features/admin';
import { useKOLApplication } from '@/features/admin/hooks/useKOLApplication';
import { KOLDetailView } from '@/features/admin/components/KOLDetailView';
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';
import { AdminSidebar, type AdminTab } from '@/features/admin/components/AdminSidebar';

/**
 * KOL detail page component.
 *
 * Displays full details of a single KOL application.
 * Requires admin authentication and valid segment.
 *
 * @component
 */
function KOLDetailPage(): ReactElement {
  const { kolId } = Route.useParams();
  const { data: kol, isLoading, error } = useKOLApplication(kolId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-500">Loading KOL details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-red-500">Error: {error.message}</div>
      </div>
    );
  }

  if (!kol) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-500">KOL not found</div>
      </div>
    );
  }

  return <KOLDetailView kol={kol} />;
}

/**
 * Route wrapper with admin guard and segment validation.
 *
 * @component
 */
function KOLDetailRoute(): ReactElement {
  const { segment } = Route.useParams();
  const navigate = useNavigate();
  const expectedSegment = import.meta.env.VITE_ADMIN_PATH_SEGMENT;

  // Validate segment matches expected value
  if (!expectedSegment || segment !== expectedSegment) {
    return <Navigate to="/" />;
  }

  const handleTabChange = (tab: AdminTab): void => {
    navigate({
      to: '/sys/mgmt/$segment',
      params: { segment },
      search: { tab },
    });
  };

  return (
    <AdminGuard>
      <SidebarProvider>
        <AdminSidebar activeTab="kol-applications" onTabChange={handleTabChange} />
        <SidebarInset className="bg-white">
          <div className="bg-white min-h-screen">
            <KOLDetailPage />
          </div>
        </SidebarInset>
      </SidebarProvider>
    </AdminGuard>
  );
}

/**
 * File-based route configuration.
 *
 * Creates route: /sys/mgmt/$segment/kols/$kolId
 */
export const Route = createFileRoute('/sys/mgmt/$segment/kols/$kolId')({
  component: KOLDetailRoute,
});
